/**
 * <collection-tabs> — Snitch-style sticky collection filter bar for the Horizon theme.
 *
 * Progressive enhancement: every tab is a real <a href> to a collection, so with
 * JS disabled the bar degrades to normal navigation. With JS, clicking a tab
 * fetches the target collection through the Section Rendering API and hot-swaps
 * the <results-list> element in place — no full page reload. Because a freshly
 * inserted <results-list> runs its own connectedCallback, Horizon's existing
 * infinite-scroll (paginated-list.js) re-initialises itself for the new
 * collection automatically; we never touch its internals.
 *
 * Attributes:
 *   data-section-id  — the section id, used to locate the matching <results-list>
 *   data-sticky      — "true" to measure and offset for a sticky theme header
 *   data-offset      — extra manual offset in px (merchant setting)
 */
class CollectionTabs extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute('data-section-id');
    this.sticky = this.getAttribute('data-sticky') === 'true';
    this.extraOffset = parseInt(this.getAttribute('data-offset') || '0', 10) || 0;

    this.addEventListener('click', this.#onClick);
    window.addEventListener('popstate', this.#onPopState);

    // Default the first tab to selected when the current page matched none of them.
    if (!this.querySelector('[role="tab"][aria-selected="true"]')) {
      const first = this.querySelector('[role="tab"]');
      if (first) {
        first.setAttribute('aria-selected', 'true');
        first.classList.add('is-active');
        first.tabIndex = 0;
      }
    }

    if (this.sticky) {
      this.#updateOffset();
      const header = document.getElementById('header-group');
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.#updateOffset);
        if (header) this.resizeObserver.observe(header);
      }
      window.addEventListener('resize', this.#updateOffset, { passive: true });
    }
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.#onPopState);
    window.removeEventListener('resize', this.#updateOffset);
    this.resizeObserver?.disconnect();
  }

  /**
   * Measure a sticky/fixed theme header so the tab bar parks just beneath it.
   * Falls back to 0 (top of viewport) plus any manual offset when the header
   * isn't sticky.
   */
  #updateOffset = () => {
    let offset = this.extraOffset;
    const header = document.getElementById('header-group');

    if (header) {
      const candidates = [header, ...header.children];
      for (const el of candidates) {
        const position = getComputedStyle(el).position;
        if (position === 'sticky' || position === 'fixed') {
          offset += el.getBoundingClientRect().height;
          break;
        }
      }
    }

    this.style.setProperty('--cgsf-sticky-offset', `${Math.round(offset)}px`);
  };

  #results() {
    return document.querySelector(`results-list[section-id="${CSS.escape(this.sectionId)}"]`);
  }

  #onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.contains(tab)) return;

    // Let modified clicks (new tab, etc.) behave natively.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

    event.preventDefault();
    if (tab.getAttribute('aria-selected') === 'true') return;

    const url = tab.getAttribute('href');
    this.#swap(url, tab, true);
  };

  #onPopState = () => {
    // Re-sync the feed when the shopper uses the browser back/forward buttons.
    const path = window.location.pathname;
    const tab = [...this.querySelectorAll('[role="tab"]')].find((t) => {
      try {
        return new URL(t.href, window.location.origin).pathname === path;
      } catch {
        return false;
      }
    });
    if (tab && tab.getAttribute('aria-selected') !== 'true') {
      this.#swap(tab.getAttribute('href'), tab, false);
    }
  };

  #setActive(activeTab) {
    for (const tab of this.querySelectorAll('[role="tab"]')) {
      const isActive = tab === activeTab;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.classList.toggle('is-active', isActive);
      tab.tabIndex = isActive ? 0 : -1;
    }
    // Keep the active tab in view within the horizontal scroller.
    activeTab?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }

  async #swap(url, tab, pushState) {
    const current = this.#results();
    if (!current || !url) return;

    this.#setActive(tab);
    current.setAttribute('aria-busy', 'true');
    current.classList.add('cgsf__results--loading');

    try {
      const fetchUrl = new URL(url, window.location.origin);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      const response = await fetch(fetchUrl.toString());
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next =
        doc.querySelector(`results-list[section-id="${CSS.escape(this.sectionId)}"]`) ||
        doc.querySelector('results-list');

      if (!next) throw new Error('No results-list in response');

      next.classList.remove('cgsf__results--loading');
      next.removeAttribute('aria-busy');
      // Inserting the parsed custom element boots its connectedCallback, which
      // re-initialises Horizon's infinite scroll for the new collection.
      current.replaceWith(next);

      // Re-scan for wishlist buttons the app hasn't initialised yet (the newly
      // injected cards). WishlistHero exposes different globals across versions,
      // so try the common ones defensively.
      try {
        const wh = window.wishlistHero || window.WishlistHero || window.whero;
        if (wh && typeof wh.init === 'function') wh.init();
        else if (typeof window.loadWishlistHero === 'function') window.loadWishlistHero();
        document.dispatchEvent(new CustomEvent('wishlisthero:reload'));
      } catch (_) {
        /* no-op */
      }

      if (pushState) history.pushState({ cgsf: true }, '', url);
    } catch (error) {
      console.error('[collection-tabs] swap failed, falling back to navigation', error);
      window.location.href = url; // graceful fallback
    }
  }
}

if (!customElements.get('collection-tabs')) {
  customElements.define('collection-tabs', CollectionTabs);
}
